import { Component, type ErrorInfo, type ReactNode } from "react";
import { useEffect, useState } from "react";
import { useRouteError } from "react-router";
import { detectLocaleFromURL } from "@/lib/locale";
import { logError } from "@/lib/logger";
import { isChunkLoadError, reloadAfterChunkError } from "@/lib/chunkRecovery";
import { useHasUnsavedChanges } from "@/hooks/unsavedChangesDialog";

type Props = {
	children: ReactNode;
};

type State = {
	hasError: boolean;
};

const COPY = {
	fr: {
		title: "Oups, quelque chose s'est cassé.",
		body: "Recharge la page. Si ça revient, garde le lien et demande de l'aide à un adulte.",
		reload: "Recharger",
		loadTitle: "Impossible de charger cette page.",
		loadBody: "Vérifie ta connexion, puis recharge pour récupérer la version actuelle de la page.",
	},
	en: {
		title: "Oops, something broke.",
		body: "Reload the page. If it happens again, keep the link and ask an adult for help.",
		reload: "Reload",
		loadTitle: "This page could not be loaded.",
		loadBody: "Check your connection, then reload to get the current version of this page.",
	},
};

function ErrorFallback({ loadError = false }: { loadError?: boolean }) {
	const copy = COPY[detectLocaleFromURL()];
	return (
		<div className="min-h-screen bg-background flex items-center justify-center p-6">
			<div className="max-w-md w-full rounded-xl border border-white/10 bg-card p-6 text-center shadow-xl">
				<h1 className="text-xl font-bold text-foreground">{loadError ? copy.loadTitle : copy.title}</h1>
				<p className="mt-3 text-sm leading-6 text-muted-foreground">{loadError ? copy.loadBody : copy.body}</p>
				<button
					type="button"
					onClick={() => window.location.reload()}
					className="mt-6 inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/80"
				>
					{copy.reload}
				</button>
			</div>
		</div>
	);
}

export function RouterErrorElement() {
	const error = useRouteError();
	const hasUnsavedChanges = useHasUnsavedChanges();
	const [canReload] = useState(() => !hasUnsavedChanges);
	useEffect(() => {
		if (canReload && !hasUnsavedChanges && reloadAfterChunkError(error)) return;
		logError("React router caught an error", error);
	}, [error, canReload, hasUnsavedChanges]);
	return <ErrorFallback loadError={isChunkLoadError(error)} />;
}

export class ErrorBoundary extends Component<Props, State> {
	state: State = { hasError: false };

	static getDerivedStateFromError(): State {
		return { hasError: true };
	}

	componentDidCatch(error: Error, info: ErrorInfo): void {
		logError("React error boundary caught an error", error, {
			componentStack: info.componentStack,
		});
	}

	render() {
		if (!this.state.hasError) return this.props.children;
		return <ErrorFallback />;
	}
}
